import * as React from "react";

/** Possible theme values */
type Theme = "dark" | "light" | "system";

/** Props for the ThemeProvider component */
type ThemeProviderProps = {
  /** The child components to render */
  children: React.ReactNode;
  /** The default theme to use if none is found in storage */
  defaultTheme?: Theme;
  /** The key used to store the theme preference in localStorage */
  storageKey?: string;
};

/** Shape of the Theme context state */
type ThemeProviderState = {
  /** The current theme */
  theme: Theme;
  /** Function to update the theme */
  setTheme: (theme: Theme) => void;
};

/** Initial state for the Theme context */
const initialState: ThemeProviderState = {
  theme: "system",
  setTheme: () => null, // Placeholder function
};

/** React context for managing theme state */
const ThemeProviderContext = React.createContext<ThemeProviderState>(initialState);

/**
 * Applies the correct theme class (light or dark) to the document root.
 * Handles the 'system' theme by checking the media query.
 * 
 * @param theme The desired theme ('light', 'dark', or 'system').
 */
const applyTheme = (theme: Theme) => {
  const root = window.document.documentElement;
  root.classList.remove("light", "dark");

  let finalTheme: 'light' | 'dark';
  if (theme === "system") {
    finalTheme = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  } else {
    finalTheme = theme;
  }
  root.classList.add(finalTheme);
};

/**
 * Provides theme state (dark, light, system) to its children via context.
 * Handles theme persistence in localStorage and applies theme classes to the document root.
 * Listens for system theme changes when the theme is set to 'system'.
 * 
 * Alternative: Consider using the `next-themes` library (already in dependencies) 
 * for potentially more features, especially if SSR is a concern.
 */
export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "vite-ui-theme",
  ...props // Allow passing other props to the context provider
}: ThemeProviderProps) {
  // Initialize state from localStorage or defaultTheme
  const [theme, setThemeState] = React.useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme
  );

  // Effect to apply the initial theme and listen for system changes
  React.useEffect(() => {
    applyTheme(theme); // Apply the theme immediately

    // If the theme is 'system', listen for changes
    if (theme === 'system') {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      
      const handleChange = () => {
        applyTheme('system'); // Re-apply system theme on change
      };

      mediaQuery.addEventListener('change', handleChange);

      // Cleanup listener on unmount or when theme changes away from 'system'
      return () => {
        mediaQuery.removeEventListener('change', handleChange);
      };
    }
  }, [theme]); // Re-run effect when theme changes

  // Memoize the context value to prevent unnecessary re-renders of consumers
  const value = React.useMemo(() => ({
    theme,
    setTheme: (newTheme: Theme) => {
      // Persist the new theme to localStorage
      localStorage.setItem(storageKey, newTheme);
      // Update the state
      setThemeState(newTheme);
    },
  }), [theme, storageKey]);

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

/**
 * Custom hook to access the current theme and the function to set it.
 * Must be used within a `ThemeProvider`.
 * 
 * @returns {ThemeProviderState} An object containing the current `theme` and `setTheme` function.
 * @throws {Error} If used outside of a `ThemeProvider`.
 */
export const useTheme = (): ThemeProviderState => {
  const context = React.useContext(ThemeProviderContext);
  
  // Ensure the hook is used within the provider
  // We need to check if setTheme is the placeholder function, since the context 
  // always has a default value
  if (context === undefined || context.setTheme === initialState.setTheme) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
    
  return context;
};
